import { useCallback, useEffect, useState } from "react";
import { getAdAnalytics } from "../../../api/ads";
import type { AdPerformanceAnalytics } from "../../../api/ads";

function toYMD(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function defaultRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

export const useAdPerformance = (adId: string) => {
  const { startDate: defaultStart, endDate: defaultEnd } = defaultRange();

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [analytics, setAnalytics] = useState<AdPerformanceAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!adId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdAnalytics({ adId, startDate, endDate });
      setAnalytics(data);
    } catch (err: any) {
      setError(err?.message || "Failed to load performance data.");
    } finally {
      setLoading(false);
    }
  }, [adId, startDate, endDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    analytics,
    loading,
    error,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    fetchAnalytics,
  };
};
